import type * as LucideIcons from 'lucide-react';
import type { SVGProps } from 'react';
import { memo } from 'react';
import { cn } from '../../common/functions/cn';
import { useLoadIcon } from '../../common/hooks/use-load-icon';

export type OdinIconName = keyof typeof LucideIcons;

export const OdinIconSize = {
  ExtraExtraSmall: 0,
  ExtraSmall: 1,
  SemiSmall: 2,
  Small: 3,
  Medium: 4,
  Large: 5,
} as const;
export type OdinIconSize = (typeof OdinIconSize)[keyof typeof OdinIconSize];

export interface OdinIconProps extends Omit<SVGProps<SVGSVGElement>, 'ref' | 'children'> {
  icon: OdinIconName;
  className?: string;
  size?: OdinIconSize;
  /**
   * Stroke width for the icon (1-3 typical). Default is 2.
   */
  strokeWidth?: number;
  spin?: boolean;
  showFocus?: boolean;
}

const OdinIcon = memo(
  ({
    icon,
    size = OdinIconSize.Small,
    className = '',
    strokeWidth = 2,
    spin = false,
    showFocus = true,
    ...props
  }: OdinIconProps) => {
    // useLoadIcon returns the actual Lucide Icon component based on icon name.
    // Lucide components accept LucideProps (size, strokeWidth, className, color, ...SVGProps).
    const Icon = useLoadIcon(icon);
    const iconSize = {
      [OdinIconSize.ExtraExtraSmall]: 10,
      [OdinIconSize.ExtraSmall]: 16,
      [OdinIconSize.SemiSmall]: 18,
      [OdinIconSize.Small]: 20,
      [OdinIconSize.Medium]: 24,
      [OdinIconSize.Large]: 32,
    };
    const iconClassNames = cn('shrink-0', className, {
      'animate-spin': spin,
      'focus:ring-0 focus:outline-hidden': showFocus,
    });

    return (
      <Icon
        size={iconSize[size]}
        strokeWidth={strokeWidth}
        className={iconClassNames}
        color="currentColor"
        {...props}
      />
    );
  }
);

OdinIcon.displayName = 'OdinIcon';
export default OdinIcon;
